import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { ShieldCheck, Unlink } from "lucide-react";
import { toast } from "sonner";
import { Button } from "./ui/button";
import SpeaqSignIn from "./SpeaqSignIn";
import { API } from "@/lib/api";
import { useTaal } from "@/contexts/TaalContext";

/**
 * Connecting a SPEAQ ID to this account, and taking it off again.
 *
 * This is where a SPEAQ ID becomes able to sign you in at all: an approval
 * proves who you are, and this panel is the moment the owner of this machine
 * says that person is them. Without it, signing in with SPEAQ ID answers
 * "not linked", which is deliberate.
 *
 * Unlinking is here from the start. A link you cannot undo is not a link, and
 * the password stays regardless, so nobody can lock themselves out here.
 */
/**
 * The way to the sign-in screen from here, next to the one in the sidebar.
 *
 * Signing out lives in the sidebar footer, where somebody looking for it will
 * look. This link is the shorter way for the one errand that starts on this
 * tab: connect a SPEAQ ID, then go and try signing in with it. It is a
 * navigation and not a reload, because reloading on a phone that arrived
 * through the tunnel tears the channel down.
 *
 * An earlier version of this comment said a sign-out would not help, on the
 * reasoning that a desktop signs its owner in regardless. That was wrong: the
 * packaged app has no single-user fallback at the gate, so a session here is
 * a real one and ending it does exactly what it says.
 */
function SignInScreenLink({ onLeave }) {
  const { t } = useTaal();
  const navigate = useNavigate();

  return (
    <button
      type="button"
      className="tap-target text-[11px] text-muted-foreground hover:text-foreground underline underline-offset-2"
      onClick={() => {
        if (onLeave) onLeave();
        navigate("/login");
      }}
      data-testid="speaq-open-signin-btn"
    >
      {t("Naar het aanmeldscherm")}
    </button>
  );
}

export default function SpeaqIdPanel({ onLeave }) {
  const { t } = useTaal();
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/auth/speaq/status`);
      setStatus(data);
    } catch {
      setStatus({ linked: false, unavailable: true });
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const link = async (sessionId) => {
    setBusy(true);
    try {
      await axios.post(`${API}/auth/speaq/link`, { session_id: sessionId });
      toast.success(t("SPEAQ ID gekoppeld"));
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || t("Die SPEAQ ID kon niet worden gekoppeld"));
    }
    setBusy(false);
  };

  const unlink = async () => {
    setBusy(true);
    try {
      await axios.delete(`${API}/auth/speaq/link`);
      toast.success(t("SPEAQ ID ontkoppeld"));
      await load();
    } catch {
      toast.error(t("Ontkoppelen is niet gelukt"));
    }
    setBusy(false);
  };

  if (!status) return null;

  if (status.linked) {
    return (
      <div className="space-y-3" data-testid="speaq-panel-linked">
        <div className="flex items-start gap-3">
          <ShieldCheck className="h-4 w-4 mt-0.5 text-ok" />
          <div className="min-w-0">
            <p className="text-sm">{t("Dit account meldt zich aan met je SPEAQ ID.")}</p>
            {/* The identifier itself, not shortened into something that looks
                like a different one. It is public by design. */}
            <p className="text-[11px] text-muted-foreground break-all font-mono mt-1">
              {status.did}
            </p>
          </div>
        </div>
        <Button
          type="button"
          variant="outline"
          className="h-11"
          disabled={busy}
          onClick={unlink}
          data-testid="speaq-unlink-btn"
        >
          <Unlink className="h-4 w-4 mr-2" />
          {t("Ontkoppelen")}
        </Button>

        {/* The way back when the identity has moved. SPEAQ makes a new DID for
            every device and every fresh install, so the one above stops
            matching the day you reinstall the app or switch phones. Connecting
            again from here replaces it, which is why this is not hidden behind
            Disconnect: on the day you need it you are already half locked out. */}
        <details className="border-t pt-3" data-testid="speaq-relink">
          <summary className="text-[11px] text-muted-foreground cursor-pointer select-none">
            {t("Nieuwe telefoon, of de SPEAQ ID-app opnieuw geinstalleerd?")}
          </summary>
          <div className="mt-3 space-y-2">
            <p className="text-[11px] text-muted-foreground">
              {t("Een nieuwe installatie is voor SPEAQ een nieuwe identiteit. Keur hier nog een keer goed, dan volgt dit account hem.")}
            </p>
            <SpeaqSignIn mode="link" onLinked={link} />
          </div>
        </details>

        <p className="text-[11px] text-muted-foreground">
          {t("Je wachtwoord blijft hoe dan ook werken, en een telefoon die je al koppelde blijft gekoppeld.")}
        </p>

        <SignInScreenLink onLeave={onLeave} />

        {/* The sign-in screen is a page like any other, so this is a normal
            navigation and not a reload. A reload here would tear down the
            channel to the Mac on a phone that arrived through the tunnel. */}
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="speaq-panel-unlinked">
      <p className="text-sm">
        {t("Koppel je SPEAQ ID om daarmee aan te melden in plaats van een wachtwoord te typen.")}
      </p>
      <SpeaqSignIn mode="link" onLinked={link} />
      <p className="text-[11px] text-muted-foreground">
        {t("Keur goed in je SPEAQ ID-app. Er verandert niets aan je wachtwoord.")}
      </p>
      <SignInScreenLink onLeave={onLeave} />
    </div>
  );
}
